import React, { useState } from 'react';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface ChangePINModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: { id: string; name: string; pin: string };
  onSave: (newPin: string) => Promise<void>;
}

const ChangePINModal: React.FC<ChangePINModalProps> = ({ isOpen, onClose, currentUser, onSave }) => {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate current PIN
    if (currentPin !== currentUser.pin) {
      setError('Current PIN is incorrect');
      return;
    }

    // Validate new PIN format
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      setError('New PIN must be exactly 4 digits');
      return;
    }

    // Check if new PIN is same as old
    if (newPin === currentUser.pin) {
      setError('New PIN must be different from current PIN');
      return;
    }

    // Validate PIN confirmation
    if (newPin !== confirmPin) {
      setError('New PIN and confirmation do not match');
      return;
    }

    setLoading(true);

    try {
      await onSave(newPin);
      // Reset form
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPin('');
    setNewPin('');
    setConfirmPin('');
    setError('');
    onClose();
  };

  const currentPinKeyboard = useSoftKeyboardField({
    id: 'change-pin-current',
    type: 'numeric',
    label: 'Current PIN',
    value: currentPin,
    onChange: (v) => setCurrentPin(v.replace(/\D/g, '').slice(0, 4)),
    manualValidator: (v) => /^\d{0,4}$/.test(v),
    maskValue: true,
  });
  const newPinKeyboard = useSoftKeyboardField({
    id: 'change-pin-new',
    type: 'numeric',
    label: 'New PIN',
    value: newPin,
    onChange: (v) => setNewPin(v.replace(/\D/g, '').slice(0, 4)),
    manualValidator: (v) => /^\d{0,4}$/.test(v),
    maskValue: true,
  });
  const confirmPinKeyboard = useSoftKeyboardField({
    id: 'change-pin-confirm',
    type: 'numeric',
    label: 'Confirm PIN',
    value: confirmPin,
    onChange: (v) => setConfirmPin(v.replace(/\D/g, '').slice(0, 4)),
    manualValidator: (v) => /^\d{0,4}$/.test(v),
    maskValue: true,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Change Lock Screen PIN</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current PIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={currentPin}
              placeholder="••••"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-pink-500"
              data-soft-keyboard-handled
              {...currentPinKeyboard}
              required
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
          </div>

          {/* New PIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={newPin}
              placeholder="••••"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-pink-500"
              data-soft-keyboard-handled
              {...newPinKeyboard}
              required
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
            <p className="text-xs text-gray-500 mt-1">Must be 4 digits (0-9)</p>
          </div>

          {/* Confirm New PIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New PIN
            </label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={confirmPin}
              placeholder="••••"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-pink-500"
              data-soft-keyboard-handled
              {...confirmPinKeyboard}
              required
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !currentPin || !newPin || !confirmPin}
              className="flex-1 px-4 py-3 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update PIN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePINModal;

